import { useEffect } from "react";
import { NavLink, Link, Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, IndianRupee, LogOut, Loader2, ShieldAlert } from "lucide-react";
import { Logo } from "../../components/Logo";
import { useAuth } from "../../context/AuthContext";

const NAV = [
  { to: "/admin/tenants", label: "Tenant Management", icon: Building2, testid: "admin-nav-tenants" },
  { to: "/admin/collections", label: "Collections", icon: IndianRupee, testid: "admin-nav-collections" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user === false) navigate("/login");
  }, [user, navigate]);

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50" data-testid="admin-loading">
        <Loader2 className="animate-spin text-blue-600" size={28} />
      </div>
    );
  }

  const doLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/");
  };

  if (user.role !== "admin") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-5" data-testid="admin-access-denied">
        <div className="card-soft max-w-md p-10 text-center">
          <span className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-amber-50 text-amber-600">
            <ShieldAlert size={26} />
          </span>
          <h1 className="text-xl font-extrabold text-slate-900">Platform admin access required</h1>
          <p className="mt-2 text-sm text-slate-500">
            {user.email} is signed in as a resident account. Sign in with a platform admin account to manage tenants and collections.
          </p>
          <button onClick={doLogout} data-testid="access-denied-logout" className="btn-primary mt-6">
            Sign out & switch account
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-layout" className="flex min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-slate-200 bg-white lg:flex" data-testid="admin-sidebar">
        <div className="border-b border-slate-100 px-6 py-5">
          <Link to="/" data-testid="admin-home-link"><Logo size={36} /></Link>
        </div>
        <nav className="flex-1 space-y-1 px-3 py-5">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${
                  isActive ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              <n.icon size={17} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-100 p-4">
          <div className="mb-3 px-2" data-testid="admin-user-info">
            <p className="text-sm font-bold text-slate-900">{user.name}</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-blue-600">Platform Admin</p>
          </div>
          <button
            onClick={doLogout}
            data-testid="admin-logout-button"
            className="flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-500 transition-colors hover:bg-red-50 hover:text-red-600"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex min-h-screen flex-1 flex-col lg:pl-64">
        <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/85 backdrop-blur-xl lg:hidden">
          <div className="flex items-center justify-between px-5 py-3">
            <Link to="/" data-testid="admin-home-link-mobile"><Logo size={32} /></Link>
            <div className="flex items-center gap-2">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
              to={n.to}
                  data-testid={`mobile-${n.testid}`}
                  className={({ isActive }) =>
                    `rounded-lg px-3 py-2 text-xs font-bold ${isActive ? "bg-blue-50 text-blue-700" : "text-slate-600"}`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <button onClick={doLogout} data-testid="admin-logout-button-mobile"
                      className="rounded-lg p-2 text-slate-500 hover:text-red-600" aria-label="Sign out">
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </header>
        <main className="flex-1 px-5 py-8 sm:px-8 lg:px-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
